import { isKnownMessage, t } from './i18n.svelte'
import type { ErrorCode } from './protocol'

export type RoomProblem = ErrorCode | 'ended' | 'connection' | 'media' | 'timeout'
export interface Problem {
  title: string
  message: string
  tone: 'quiet' | 'warning' | 'error'
  retry: boolean
  terminal?: boolean
  detail?: string
}

/** Stable states drive the UI; server text is diagnostic information only. */
export function roomProblem(kind: RoomProblem, detail?: string): Problem {
  const common = { tone: 'error' as const, retry: true, detail }
  switch (kind) {
    case 'timeout':
      return {
        ...common,
        tone: 'warning',
        title: t('The room is taking too long to respond'),
        message: t(
          'Try again, or ask your friend to check that sharing is still running and send their current link.',
        ),
      }
    case 'ended':
      return {
        ...common,
        tone: 'quiet',
        retry: false,
        terminal: true,
        title: t('The host ended this share'),
        message: t(
          'Thanks for watching. If your friend shares again, join from the friends list or ask for their new link.',
        ),
      }
    case 'room_not_found':
      return {
        ...common,
        tone: 'quiet',
        retry: false,
        terminal: true,
        title: t('This room is no longer available'),
        message: t(
          'The share may have ended or the link may have changed. Open your friends list or ask the host for a new link.',
        ),
      }
    case 'connection':
      return {
        ...common,
        tone: 'warning',
        title: t('Reconnecting to your friend…'),
        message: t(
          'The connection was interrupted. Sofa is trying again and will follow a new share link when your friend comes back.',
        ),
      }
    case 'media':
      return {
        ...common,
        tone: 'warning',
        title: t('The video could not connect'),
        message: t(
          'Try reconnecting. If it keeps happening, ask the host to restart sharing and send the new link.',
        ),
      }
    case 'bad_password':
      return {
        ...common,
        retry: false,
        title: t('Wrong password'),
        message: t('Check the password with the host, then enter it below and join again.'),
      }
    case 'room_full':
      return {
        ...common,
        tone: 'warning',
        title: t('The room is full'),
        message: t(
          'All seats are taken. Try again when someone leaves, or ask the host to increase the viewer limit.',
        ),
      }
    case 'protocol_mismatch':
      return {
        ...common,
        retry: false,
        title: t('An update is needed'),
        message: t(
          'Update Sofa, or refresh this page in your browser. The host may need to update too.',
        ),
      }
    case 'rate_limited':
      return {
        ...common,
        retry: false,
        title: t('Too many attempts'),
        message: t('Wait a moment before joining again.'),
      }
    case 'not_permitted':
      return {
        ...common,
        retry: false,
        title: t('You cannot join this room'),
        message: t('Ask the host for permission and their current share link.'),
      }
    default:
      return {
        ...common,
        title: t('Could not join the room'),
        message: t(
          'Try again. If the problem continues, ask the host for their current share link.',
        ),
      }
  }
}

/** Native errors stay available for support without becoming the headline. */
export function appProblem(
  error: string,
  context: 'sharing' | 'link' | 'account' | 'settings' | 'update',
): Problem {
  const translated = t(error)
  if (context === 'update') {
    if (error === 'stop the stream before updating') {
      return {
        title: t('Finish sharing before updating'),
        message: t('Stop your current share, then choose Update and restart.'),
        tone: 'quiet',
        retry: false,
      }
    }
    return {
      title: t('The update could not finish'),
      message: t(
        'Check your connection and try again from Settings. You can keep using your current version.',
      ),
      tone: 'warning',
      retry: false,
      detail: error,
    }
  }
  // Existing deliberate user-facing validation and account messages retain their meaning.
  const accountMessages = [
    'You are not friends yet.',
    'They are offline.',
    'That request is gone.',
    'Too many friends or requests.',
    'The server did not understand that.',
    'Your login is not valid any more. Sign in again.',
    'Someone already has that handle.',
    'That handle is not allowed.',
    'You changed your handle recently. Try again later.',
    'This server cannot check logins right now.',
    'You signed in on another device, so this one was disconnected.',
    'Another Sofa connection replaced this one. Reconnect here to use friends.',
    'Ctrl+Alt types characters on many keyboards. Pick another combination.',
  ]
  if (
    context === 'link' ||
    isKnownMessage(error) ||
    accountMessages.some((message) => error === message || error === t(message)) ||
    /^(Pick a window|Choose an audio app)/.test(error)
  ) {
    return {
      title: t(
        context === 'link'
          ? 'Check the room link'
          : context === 'settings'
            ? 'Could not save this setting'
            : context === 'account'
              ? 'Account needs attention'
              : 'Check your sharing settings',
      ),
      message: translated,
      tone: 'error',
      retry: false,
    }
  }
  if (context === 'account') {
    if (/invalid login credentials/i.test(error)) {
      return {
        title: t('Check your email and password'),
        message: t('Those details did not match an account. Check them and try again.'),
        tone: 'error',
        retry: false,
      }
    }
    if (/email not confirmed/i.test(error)) {
      return {
        title: t('Confirm your email first'),
        message: t('Open the confirmation email, then return to Sofa and sign in again.'),
        tone: 'quiet',
        retry: false,
      }
    }
    if (/access_denied|cancelled|canceled/i.test(error)) {
      return {
        title: t('Sign-in was cancelled'),
        message: t('You can try signing in again whenever you are ready.'),
        tone: 'quiet',
        retry: false,
      }
    }
    return {
      title: t('Account connection needs attention'),
      message: t(
        'Check your internet connection and try again. If your session expired, sign in again to reconnect your friends list.',
      ),
      tone: 'error',
      retry: false,
      detail: error,
    }
  }
  if (context === 'settings') {
    return {
      title: t('Could not save this setting'),
      message: t(
        'Try a different value and save again. If it keeps failing, restart Sofa and try again.',
      ),
      tone: 'error',
      retry: false,
      detail: error,
    }
  }
  if (/window.*(closed|gone|invalid)|capture.*(closed|lost)|invalid.*window/i.test(error)) {
    return {
      title: t('The shared window is unavailable'),
      message: t('Open the window again and select it in Sofa to continue sharing.'),
      tone: 'warning',
      retry: false,
      detail: error,
    }
  }
  if (/connect|network|tunnel|timed? out|timeout|dns|websocket/i.test(error)) {
    return {
      title: t('Sharing could not connect'),
      message: t(
        'Check your internet connection, then try again. You can also choose another connection mode in stream settings.',
      ),
      tone: 'warning',
      retry: false,
      detail: error,
    }
  }
  if (/permission|access.*denied|not allowed/i.test(error)) {
    return {
      title: t('Sofa needs permission'),
      message: t(
        'Check screen and microphone permissions in your system settings, then try again.',
      ),
      tone: 'error',
      retry: false,
      detail: error,
    }
  }
  return {
    title: t('Sharing needs attention'),
    message: t(
      'Try the action again. If it keeps failing, choose the window again or restart sharing.',
    ),
    tone: 'error',
    retry: false,
    detail: error,
  }
}
